import { watch } from "@arcgis/core/core/reactiveUtils";
import MapView from "@arcgis/core/views/MapView";
import { WorldMapView } from "@components/world-map/world-map-types";
import { isNumber } from "es-toolkit";
import { RefObject, useEffect, useEffectEvent } from "react";

/**
 * Reports the map center and zoom level whenever the map comes to rest after a
 * pan or zoom. Relies on the ArcGIS `stationary` flag so the callback is only
 * invoked once the user stops moving the map, not on every intermediate frame.
 *
 * @param viewRef - Ref to the ArcGIS MapView instance.
 * @param isViewReady - Whether the ArcGIS view has already been initialized.
 * @param onViewStable - Callback invoked with the current center/zoom once the map is stationary.
 */
export function useMapViewSync(
  viewRef: RefObject<MapView | null>,
  isViewReady: boolean,
  onViewStable?: (view: WorldMapView) => void
): void {
  const emitStableView = useEffectEvent((): void => {
    const view = viewRef.current;
    const center = view?.center;
    if (!view || !center || !onViewStable) {
      return;
    }

    // ArcGIS types the center coordinates as nullable, so guard before reporting.
    const { latitude, longitude } = center;
    if (!isNumber(latitude) || !isNumber(longitude)) {
      return;
    }

    onViewStable({
      lat: latitude,
      lng: longitude,
      zoom: view.zoom,
    });
  });

  useEffect(() => {
    const view = viewRef.current;
    if (!isViewReady || !view) {
      return;
    }

    const handle = watch(
      () => view.stationary,
      (isStationary) => {
        if (isStationary) {
          emitStableView();
        }
      }
    );

    return () => handle.remove();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewReady, viewRef]);
}
